// Demo API wrapper that provides CRUD operations for demo data
import { Expense, Income, Debt, Goal } from "../../lib/types";
import { demoService } from "../demo/demoService";
import { v4 as uuidv4 } from "uuid";

// Store demo data modifications in session storage
const DEMO_STORAGE_KEY = "finance_demo_data";

class DemoAPI {
  private data: any;

  constructor() {
    this.loadData();
  }

  private buildFreshData() {
    const now = new Date();
    return {
      ...demoService.getAllDemoData(),
      __meta: {
        generatedAt: now.toISOString(),
        month: now.getMonth(),
        year: now.getFullYear(),
      },
    };
  }

  private isStale(data: any): boolean {
    const meta = data?.__meta;
    if (!meta || typeof meta !== "object") {
      return true;
    }
    const now = new Date();
    return meta.month !== now.getMonth() || meta.year !== now.getFullYear();
  }

  private ensureFreshData() {
    if (!this.data || this.isStale(this.data)) {
      this.data = this.buildFreshData();
      this.saveData();
    }
  }

  private loadData() {
    // Try to load modified demo data from session storage
    const stored = sessionStorage.getItem(DEMO_STORAGE_KEY);
    if (stored) {
      this.data = JSON.parse(stored);
      if (this.isStale(this.data)) {
        this.data = this.buildFreshData();
        this.saveData();
      }
    } else {
      // Load fresh demo data
      this.data = this.buildFreshData();
      this.saveData();
    }
  }

  private saveData() {
    sessionStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(this.data));
  }

  resetDemoData() {
    this.data = this.buildFreshData();
    this.saveData();
  }

  // Expense operations
  getExpenses(): Expense[] {
    this.ensureFreshData();
    return this.data.expenses || [];
  }

  getExpenseById(id: string): Expense | null {
    this.ensureFreshData();
    return this.data.expenses.find((e: Expense) => e.id === id) || null;
  }

  addExpense(expense: Omit<Expense, "id">): Expense {
    this.ensureFreshData();
    const newExpense = {
      ...expense,
      id: uuidv4(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    this.data.expenses.push(newExpense);
    this.saveData();
    return newExpense;
  }

  updateExpense(expense: Expense): Expense {
    this.ensureFreshData();
    const index = this.data.expenses.findIndex(
      (e: Expense) => e.id === expense.id
    );
    if (index !== -1) {
      this.data.expenses[index] = {
        ...expense,
        updated_at: new Date().toISOString(),
      };
      this.saveData();
      return this.data.expenses[index];
    }
    throw new Error("Expense not found");
  }

  deleteExpense(id: string): void {
    this.ensureFreshData();
    this.data.expenses = this.data.expenses.filter((e: Expense) => e.id !== id);
    this.saveData();
  }

  toggleExpensePaid(id: string): Expense {
    this.ensureFreshData();
    const expense = this.data.expenses.find((e: Expense) => e.id === id);
    if (expense) {
      expense.is_paid = !expense.is_paid;
      expense.updated_at = new Date().toISOString();
      this.saveData();
      return expense;
    }
    throw new Error("Expense not found");
  }

  // Income operations
  getIncome(): Income[] {
    this.ensureFreshData();
    return this.data.income || [];
  }

  getIncomeById(id: string): Income | null {
    this.ensureFreshData();
    return this.data.income.find((i: Income) => i.id === id) || null;
  }

  addIncome(income: Omit<Income, "id">): Income {
    this.ensureFreshData();
    const newIncome = {
      ...income,
      id: uuidv4(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    this.data.income.push(newIncome);
    this.saveData();
    return newIncome;
  }

  updateIncome(income: Income): Income {
    this.ensureFreshData();
    const index = this.data.income.findIndex((i: Income) => i.id === income.id);
    if (index !== -1) {
      this.data.income[index] = {
        ...income,
        updated_at: new Date().toISOString(),
      };
      this.saveData();
      return this.data.income[index];
    }
    throw new Error("Income not found");
  }

  deleteIncome(id: string): void {
    this.ensureFreshData();
    this.data.income = this.data.income.filter((i: Income) => i.id !== id);
    this.saveData();
  }

  toggleIncomePaid(id: string): Income {
    this.ensureFreshData();
    const income = this.data.income.find((i: Income) => i.id === id);
    if (income) {
      income.is_paid = !income.is_paid;
      income.updated_at = new Date().toISOString();
      this.saveData();
      return income;
    }
    throw new Error("Income not found");
  }

  // Debt operations
  getDebts(): Debt[] {
    this.ensureFreshData();
    return this.data.debts || [];
  }

  getDebtById(id: string): Debt | null {
    this.ensureFreshData();
    return this.data.debts.find((d: Debt) => d.id === id) || null;
  }

  addDebt(debt: Omit<Debt, "id">): Debt {
    this.ensureFreshData();
    const newDebt = {
      ...debt,
      id: uuidv4(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    this.data.debts.push(newDebt);
    this.saveData();
    return newDebt;
  }

  updateDebt(debt: Debt): Debt {
    this.ensureFreshData();
    const index = this.data.debts.findIndex((d: Debt) => d.id === debt.id);
    if (index !== -1) {
      this.data.debts[index] = {
        ...debt,
        updated_at: new Date().toISOString(),
      };
      this.saveData();
      return this.data.debts[index];
    }
    throw new Error("Debt not found");
  }

  deleteDebt(id: string): void {
    this.ensureFreshData();
    this.data.debts = this.data.debts.filter((d: Debt) => d.id !== id);
    this.saveData();
  }

  // Goal operations
  getGoals(): Goal[] {
    this.ensureFreshData();
    return this.data.goals || [];
  }

  getGoalById(id: string): Goal | null {
    this.ensureFreshData();
    return this.data.goals?.find((g: Goal) => g.id === id) || null;
  }

  addGoal(goal: Omit<Goal, "id" | "createdAt" | "updatedAt">): Goal {
    this.ensureFreshData();
    if (!this.data.goals) {
      this.data.goals = [];
    }
    const now = new Date();
    const newGoal: Goal = {
      ...goal,
      id: uuidv4(),
      createdAt: now,
      updatedAt: now,
    };
    this.data.goals.push(newGoal);
    this.saveData();
    return newGoal;
  }

  updateGoal(goal: Goal): Goal {
    this.ensureFreshData();
    const index =
      this.data.goals?.findIndex((g: Goal) => g.id === goal.id) ?? -1;
    if (index !== -1) {
      this.data.goals[index] = {
        ...goal,
        updatedAt: new Date(),
      };
      this.saveData();
      return this.data.goals[index];
    }
    throw new Error("Goal not found");
  }

  deleteGoal(id: string): void {
    this.ensureFreshData();
    if (this.data.goals) {
      this.data.goals = this.data.goals.filter((g: Goal) => g.id !== id);
      this.saveData();
    }
  }

  updateGoalLinkedAccounts(goalId: string, accountIds: string[]): Goal {
    this.ensureFreshData();
    const goal = this.data.goals?.find((g: Goal) => g.id === goalId);
    if (goal) {
      goal.linkedAccountIds = accountIds;
      goal.updatedAt = new Date();
      this.saveData();
      return goal;
    }
    throw new Error("Goal not found");
  }

  // Account operations
  getAccounts(): any[] {
    this.ensureFreshData();
    return this.data.accounts || [];
  }

  // Transaction operations
  getTransactions(): any[] {
    this.ensureFreshData();
    return this.data.transactions || [];
  }
}

// Export singleton instance
export const demoAPI = new DemoAPI();
